import { useEffect, useState } from "react";
import { Flame } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";

// Categories where the spice picker should NOT appear.
// Categories where items DO NOT get a spice picker by default. Individual
// items in these categories can still opt-in via SPICE_ITEM_ALLOWLIST below.
const NO_SPICE_CATEGORIES = new Set([
  "desserts",
  "drinks",
  "beverages",
  "tandoori-breads",
  "salads",
  "wings-salads",
  "sides-sauces",
  "tandoori-grills",
  "combo-meals",
  // Restaurant-owner decisions:
  // • Appetizers as a whole DON'T take spice — except the four Indo-Chinese
  //   options we explicitly allow-list below.
  // • Pizzas never take spice.
  "appetizers",
  "pizzas",
]);

// Item names (case-insensitive contains match) that OVERRIDE their category
// default. Use for items in a "no-spice" category that should still show
// the spice picker.
const SPICE_ITEM_ALLOWLIST = [
  "chicken manchurian",
  "vegetable manchurian",
  "chilli gobhi",
  "chilli paneer",
];

// Item names (case-insensitive contains match) that OVERRIDE their category
// default the other way — plain / simply-flavoured items in a spice-eligible
// category (e.g. Rice Specialties) that should NOT prompt for a spice level.
// Biryanis stay spice-eligible; only the plain sides listed here are excluded.
const SPICE_ITEM_DENYLIST = [
  "steamed rice",
  "saffron rice",
  "coconut rice",
  "jeera rice",
  "kashmiri pulao rice",
];

export const SPICE_LEVELS = [
  { id: "mild", label: "Mild", flames: 0, hint: "No heat — kid-friendly" },
  { id: "mild_medium", label: "Mild Medium", flames: 1, hint: "Just a whisper of heat" },
  { id: "medium_hot", label: "Medium Hot", flames: 2, hint: "Warm, balanced (default)" },
  { id: "hot", label: "Hot", flames: 3, hint: "How most regulars order" },
  { id: "extra_hot", label: "Extra Hot", flames: 4, hint: "Bring water." },
  { id: "indian_hot", label: "Indian Hot", flames: 5, hint: "Authentic Punjabi heat — only if you know." },
];

// Default spice level when adding spice-eligible items to cart.
export const DEFAULT_SPICE = "medium_hot";

// Legacy aliases so older menu items / orders that stored the original 4-level
// scale (mild/medium/spicy/extra) still render and don't break.
const SPICE_ALIAS = {
  medium: "medium_hot",
  spicy: "hot",
  extra: "extra_hot",
};
export const normalizeSpice = (id) => (id ? SPICE_ALIAS[id] || id : id);
export const spiceLabel = (id) => {
  const norm = normalizeSpice(id);
  return SPICE_LEVELS.find((s) => s.id === norm)?.label || id;
};

export const itemTakesSpice = (item) => {
  if (!item) return false;
  const name = (item.name || "").toLowerCase();
  if (SPICE_ITEM_DENYLIST.some((d) => name.includes(d))) return false;
  if (SPICE_ITEM_ALLOWLIST.some((a) => name.includes(a))) return true;
  return !NO_SPICE_CATEGORIES.has(item.category_slug);
};

export const DIP_OPTIONS = ["Butter dip", "Mint dip", "Raita dip"];

/**
 * Variant + Spice picker. Always renders if `open`. Auto-handles items
 * with no variants (no size selection shown) and items in non-spice
 * categories (no spice selection shown).
 *
 * onAdd is called with { item_id, variant_name, name, variant_label, price, spice_level, dip }
 */
export default function VariantPicker({ item, open, onClose, onAdd }) {
  const [selected, setSelected] = useState(0);
  const [spice, setSpice] = useState(DEFAULT_SPICE);
  const [dip, setDip] = useState(null);

  useEffect(() => {
    if (open) {
      setSelected(0);
      setSpice(DEFAULT_SPICE);
      setDip(null);
    }
  }, [open, item]);

  if (!item) return null;
  const variant = item.variants?.[selected];
  const showVariants = !!item.variants?.length;
  const showSpice = itemTakesSpice(item);
  const showDip = !!item.dip_choice;
  const price = showVariants ? variant?.price : item.price;
  const canAdd = price != null && (!showDip || !!dip);

  const submit = () => {
    if (!canAdd) return;
    onAdd({
      item_id: item.id,
      name: item.name,
      variant_name: variant?.name || null,
      variant_label: variant?.name || null,
      price,
      spice_level: showSpice ? spice : null,
      dip: showDip ? dip : null,
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? onClose() : null)}>
      <DialogContent className="sm:max-w-md" data-testid="variant-picker">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold text-[#0E0E1A]">
            {item.name}
          </DialogTitle>
          {item.desc && (
            <DialogDescription className="text-[#57534E] leading-relaxed">
              {item.desc}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="mt-2 space-y-5">
          {showVariants && (
            <section>
              <div className="text-xs tracking-[0.2em] uppercase text-[#C9853E] font-bold mb-3">
                {item.note || "Choose a size"}
              </div>
              <div className="grid gap-2" data-testid="variant-options">
                {item.variants.map((v, i) => (
                  <button
                    key={i}
                    data-testid={`variant-option-${i}`}
                    type="button"
                    onClick={() => setSelected(i)}
                    className={`flex items-center justify-between rounded-2xl px-4 py-3.5 border transition-all text-left ${
                      selected === i
                        ? "border-[#C9853E] bg-[#C9853E]/10"
                        : "border-white/40 bg-white/40 hover:bg-white/60"
                    }`}
                  >
                    <span className="font-semibold text-[#0E0E1A]">{v.name}</span>
                    <span className="font-display font-bold text-[#C9853E]">
                      ${v.price.toFixed(2)}
                    </span>
                  </button>
                ))}
              </div>
            </section>
          )}

          {showSpice && (
            <section data-testid="spice-picker">
              <div className="text-xs tracking-[0.2em] uppercase text-[#C9853E] font-bold mb-3 flex items-center gap-1.5">
                <Flame className="w-3.5 h-3.5" /> Spice level
              </div>
              <div className="grid grid-cols-2 gap-2">
                {SPICE_LEVELS.map((lvl) => {
                  const active = spice === lvl.id;
                  return (
                    <button
                      key={lvl.id}
                      type="button"
                      onClick={() => setSpice(lvl.id)}
                      data-testid={`spice-${lvl.id}`}
                      className={`rounded-2xl px-3 py-3 border text-left transition-all ${
                        active
                          ? "border-[#C9853E] bg-[#C9853E]/10 shadow-sm"
                          : "border-white/40 bg-white/40 hover:bg-white/60"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-[#0E0E1A] text-sm">{lvl.label}</span>
                        <span className="flex">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Flame
                              key={i}
                              className={`w-3 h-3 ${
                                i < lvl.flames ? "text-[#C9853E] fill-[#C9853E]" : "text-[#E7E5E4]"
                              }`}
                            />
                          ))}
                        </span>
                      </div>
                      <div className="text-[10px] text-[#57534E] mt-0.5 leading-tight">
                        {lvl.hint}
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {showDip && (
            <section data-testid="dip-picker">
              <div className="text-xs tracking-[0.2em] uppercase text-[#C9853E] font-bold mb-2 flex items-center gap-2">
                Free dip <span className="text-[#57534E] font-normal normal-case tracking-normal">— pick one, included</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {DIP_OPTIONS.map((opt) => {
                  const active = dip === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setDip(opt)}
                      data-testid={`dip-${opt.split(" ")[0].toLowerCase()}`}
                      className={`rounded-2xl px-3 py-3 border text-center transition-all ${
                        active
                          ? "border-[#C9853E] bg-[#C9853E]/10 shadow-sm"
                          : "border-[#E7E5E4] bg-white hover:bg-[#F5F5F4]"
                      }`}
                    >
                      <div className="font-bold text-[#0E0E1A] text-sm">
                        {opt.replace(" dip", "")}
                      </div>
                      <div className="text-[10px] text-[#57534E] mt-0.5">Free</div>
                    </button>
                  );
                })}
              </div>
              {!dip && (
                <div className="text-[11px] text-[#57534E] mt-2" data-testid="dip-picker-hint">
                  Pick a dip to continue.
                </div>
              )}
            </section>
          )}

          <button
            data-testid="variant-add-btn"
            type="button"
            onClick={submit}
            disabled={!canAdd}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#C9853E] hover:bg-[#A6692D] disabled:opacity-50 text-white rounded-full px-6 py-3.5 font-bold transition-all shadow-[0_8px_20px_-8px_rgba(201,133,62,0.6)]"
          >
            Add to cart {price != null && `· $${price.toFixed(2)}`}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
